import { extractText, getSoilLabel, getKleurLabel, getKleurSterkte, getMediaanLabel, formatKleur, formatBzb } from '../codelists/lookup.js';
import * as nen6693 from '../codelists/nen6693.js';
import type { JsonLaag, Layer, BzbItem } from '../types/index.js';
import { unwrapNumber, unwrapName, jsonLaagToLayer, layerToJsonLaag } from '../types/index.js';

/**
 * Framework-agnostic model wrapping a raw JsonLaag.
 * Used by both the backoffice and can be instantiated in the backend.
 */
export class LayerModel {
  readonly id: string | null;            // DB row id (set when loaded from normalized tables)

  readonly upperDepth: number;
  readonly lowerDepth: number;
  readonly thickness: number;

  readonly soilType: string | null;      // SIKB code
  readonly soilLabel: string | null;     // human label

  readonly mediaan: string | null;       // SIKB code
  readonly mediaanLabel: string | null;

  readonly kleurMain: string | null;
  readonly kleurShade: string | null;
  readonly kleurSub: string | null;
  readonly kleurLabel: string | null;    // formatted human string

  readonly toevoeging1: string | null;
  readonly gradatie1: string | null;
  readonly toevoeging2: string | null;
  readonly gradatie2: string | null;
  readonly toevoeging3: string | null;
  readonly gradatie3: string | null;

  readonly bzb: BzbItem[];
  readonly bzbLabels: string[];

  readonly opmerking: string | null;
  readonly meetpuntName: string | null;
  readonly meetpuntGuid: string | null;

  readonly raw: JsonLaag;

  constructor(data: JsonLaag) {
    this.raw = data;
    this.id = extractText(data.guid) ?? null;

    this.upperDepth = unwrapNumber(data.upperDepth_cm) ?? 0;
    this.lowerDepth = unwrapNumber(data.lowerDepth_cm) ?? 0;
    this.thickness  = Math.max(0, this.lowerDepth - this.upperDepth);

    // Soil type — prefer sikb_id over waarde (which may be a human name from AI)
    const rawSoil = data.grondsoort;
    this.soilType  = (rawSoil?.sikb_id ?? extractText(rawSoil)) || null;
    this.soilLabel = getSoilLabel(this.soilType) ?? this.soilType;

    // De samenvattende zandklassen 'fijn' en 'middelgrof' bestaan niet meer als keuze; wat er
    // nog mee is opgeslagen tonen we als de subklasse waarin ze opgaan. Zonder dit staat er
    // bij een oude laag een mediaan die in geen enkele keuzelijst meer voorkomt.
    this.mediaan      = nen6693.normalizeMediaan(this.soilType, unwrapName(data.mediaan) ?? null);
    this.mediaanLabel = getMediaanLabel(this.mediaan);

    this.kleurMain  = data.kleur?.ti_color_main ?? null;
    this.kleurShade = data.kleur?.ti_color_shade ?? null;
    this.kleurSub   = data.kleur?.ti_color_sub ?? null;
    this.kleurLabel = formatKleur(data.kleur ?? null);

    this.toevoeging1 = data.bijmengingen?.[0]?.toevoeging ?? null;
    this.gradatie1   = data.bijmengingen?.[0]?.gradatie ?? null;
    this.toevoeging2 = data.bijmengingen?.[1]?.toevoeging ?? null;
    this.gradatie2   = data.bijmengingen?.[1]?.gradatie ?? null;
    this.toevoeging3 = data.bijmengingen?.[2]?.toevoeging ?? null;
    this.gradatie3   = data.bijmengingen?.[2]?.gradatie ?? null;

    this.bzb = (data.bzb ?? []).map(b => ({
      code:      b.ti_bzb_type ?? null,
      type:      b.type_naam ?? null,
      gradation: b.ti_bzb_gradation ?? null,
      origin:    b.ti_bzb_origin ?? null,
    }));
    this.bzbLabels = (data.bzb ?? []).map(b => formatBzb(b)).filter(Boolean);

    this.opmerking    = extractText(data.opmerking) || null;
    this.meetpuntName = unwrapName(data.meetpuntName) ?? null;
    this.meetpuntGuid = extractText(data.meetpuntGuid) || null;
  }

  /** Soil colour hex for visual boring log. */
  get colorHex(): string {
    const s = (this.soilType ?? '').toLowerCase();
    if (s === 'z' || s.startsWith('z')) return '#F0C060';
    if (s === 'k' || s.startsWith('k')) return '#7FBFBF';
    if (s === 'l' || s.startsWith('l')) return '#C8A882';
    if (s === 'v' || s.startsWith('v')) return '#8B6914';
    if (s === 'g' || s.startsWith('g')) return '#C0A080';
    if (s === 's' || s === 'si')        return '#A8C8A8';
    return '#B5B5C3';
  }

  /** Convert to normalized flat Layer (for API / DB). */
  toLayer(): Layer {
    return jsonLaagToLayer(this.raw);
  }

  /**
   * Construct a LayerModel from a normalized flat Layer DB row.
   * @param layer  Flat Layer object (from DB or API response)
   * @param boringName  Name of the parent boring (used for meetpuntName matching)
   */
  static fromFlat(layer: Layer, boringName = ''): LayerModel {
    return new LayerModel(layerToJsonLaag(layer, boringName));
  }
}
